/**
 * The server-owned persistence + INDEPENDENT fresh-readback seam.
 *
 * Generic Application Ontology Program — Studio Task 5.
 *
 * WHAT THIS IS. One normal write (binding + sealed evidence envelope +
 * envelope ref + generated application-package bytes + generated artifact
 * bytes), followed by one DISTINCT authenticated `loadProject()` that
 * reconstructs the whole identity DAG and the exact bytes from what the
 * server actually holds, and only then a readback observation. Completion is
 * downstream of that observation being appended, never upstream of it.
 *
 * THE THREE THINGS THE BROWSER MAY NOT DO, and why each is spelled out:
 *
 *   1. It may not supply the envelope BODY. The envelope is constructed here
 *      from the immutable binding, the two refs computed from the exact bytes
 *      in hand, the exact validation-observation ref and the verifier-issued
 *      persistence-eligibility ref. `createServerOwnedApplicationGenerationEvidenceEnvelope`
 *      takes no `contentHash` and no envelope-shaped input at all, so there is
 *      no parameter through which one could arrive.
 *   2. It may not supply `modelInvocationRef`. That ref exists only because a
 *      model actually ran; its only legitimate source is Task 3's verified
 *      terminal result evidence. `assertServerOnlyPersistenceSubject()` below
 *      refuses a subject that carries the field at all — an offered value is
 *      refused rather than ignored, because ignoring it silently is how a
 *      forged field becomes a field nobody reads.
 *   3. It may not choose a content hash. Every hash on this lane is computed
 *      from the bytes or from the canonical body, here.
 *
 * WHY THE READBACK IS A SEPARATE CALL. A comparison against the object the
 * writer already held proves the writer is self-consistent, which is not the
 * question. The question is whether the SERVER holds it, so the comparison
 * subject has to come back over the wire after the write, from an
 * authenticated read this seam issues itself.
 *
 * WHY BYTES ARE COMPARED AS BYTES. `toEqual` on two JSON projections of the
 * same application is satisfied by a re-serialization that moved a key, and a
 * one-byte change inside an opaque package is invisible to it entirely. Both
 * byte arrays are therefore compared element by element, and independently
 * re-hashed against the refs the envelope carries.
 */
import type { ContentHash, ContentRef } from '@cogo/publisher-core'
import {
  hashCanonicalContent,
  stableCanonicalStringify,
} from '@cogo/schema/canonical-content-hash'
import {
  assertApplicationGenerationEvidenceEnvelopeIntegrity,
  type ApplicationGenerationEvidenceEnvelope,
} from '@cogo/schema/application-generation-evidence-envelope'
import type {
  ApplicationEvidenceRole,
  ApplicationEvidenceRoleResolver,
  ApplicationOntologyGenerationBinding,
} from '@cogo/schema/application-ontology-generation-binding'
import {
  assertGeneratedApplicationReadbackObservationIntegrity,
  type GeneratedApplicationReadbackObservation,
} from '@cogo/schema/generated-application-readback-observation'

import type { LoadedApp } from '@/features/page-editor/store/types'

// ─── refusals ───────────────────────────────────────────────────────────

export const GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES = {
  /** A browser-reachable field tried to set a server-only identity. */
  SERVER_ONLY_FIELD_OFFERED: 'SERVER_ONLY_FIELD_OFFERED',
  /** The generated package address collided with an input or the artifact. */
  APPLICATION_PACKAGE_ROLE_COLLISION: 'APPLICATION_PACKAGE_ROLE_COLLISION',
  /** The sealed envelope did not satisfy its own closed contract. */
  GENERATION_EVIDENCE_ENVELOPE_INVALID: 'GENERATION_EVIDENCE_ENVELOPE_INVALID',
  /** The write acknowledged an envelope ref that is not this envelope's. */
  PERSISTENCE_WRITE_REF_DRIFT: 'PERSISTENCE_WRITE_REF_DRIFT',
  /** The fresh read carries no generation evidence at all. */
  GENERATION_EVIDENCE_ABSENT: 'GENERATION_EVIDENCE_ABSENT',
  /** The fresh read carries evidence that is not what was sealed. */
  GENERATION_EVIDENCE_READBACK_DRIFT: 'GENERATION_EVIDENCE_READBACK_DRIFT',
  /** A readback byte array is not the persisted one, byte for byte. */
  GENERATED_APPLICATION_BYTES_DRIFT: 'GENERATED_APPLICATION_BYTES_DRIFT',
  /** A compared evidence role disagreed across the write/read boundary. */
  EVIDENCE_ROLE_READBACK_DRIFT: 'EVIDENCE_ROLE_READBACK_DRIFT',
  /** Blocks / block identities did not survive the authenticated roundtrip. */
  GENERATED_APPLICATION_READBACK_DRIFT: 'GENERATED_APPLICATION_READBACK_DRIFT',
  /** The append of the readback observation did not observe this observation. */
  READBACK_OBSERVATION_APPEND_DRIFT: 'READBACK_OBSERVATION_APPEND_DRIFT',
  /**
   * A BOUND generation completed without Task 3's terminal result evidence.
   * This is the missing-receipt compatibility path, and it is closed: a
   * generation with no receipt has no provable model invocation, so there is
   * nothing honest to seal an envelope from.
   */
  TERMINAL_RESULT_EVIDENCE_ABSENT: 'TERMINAL_RESULT_EVIDENCE_ABSENT',
  /** Terminal result evidence arrived, but not about THIS generation. */
  TERMINAL_RESULT_EVIDENCE_INVALID: 'TERMINAL_RESULT_EVIDENCE_INVALID',
  /** The port that must record the readback observation was not supplied. */
  READBACK_OBSERVATION_SINK_ABSENT: 'READBACK_OBSERVATION_SINK_ABSENT',
} as const

export type GeneratedApplicationPersistenceRefusalCode =
  (typeof GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES)[keyof typeof GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES]

export class StudioGeneratedApplicationPersistenceRefusal extends Error {
  readonly code: GeneratedApplicationPersistenceRefusalCode
  readonly detail: string
  constructor(code: GeneratedApplicationPersistenceRefusalCode, detail = '') {
    super(detail ? `${code}:${detail}` : code)
    this.name = 'StudioGeneratedApplicationPersistenceRefusal'
    this.code = code
    this.detail = detail
  }
}

function refuse(
  code: GeneratedApplicationPersistenceRefusalCode,
  detail = '',
): never {
  throw new StudioGeneratedApplicationPersistenceRefusal(code, detail)
}

// ─── the ordered persistence lane ───────────────────────────────────────

/**
 * The ONE legal order. It EXTENDS Task 4's admission order rather than
 * replacing it: Task 4's terminal `persist` step is this list's element 2
 * (`persist-blocks-envelope-package-and-artifact-bytes`), and Task 4's whole
 * completed admission collapses into this list's element 0
 * (`artifact-valid-admission`). Expressed as a constant so a spy can be
 * compared against it, rather than as prose that can stop being true.
 */
export const GENERATED_APPLICATION_PERSISTENCE_ORDER = [
  'artifact-valid-admission',
  'seal-generation-evidence-envelope',
  'persist-blocks-envelope-package-and-artifact-bytes',
  'fresh-load-project',
  'append-readback-observation',
  'mark-generation-complete',
] as const

export type GeneratedApplicationPersistenceStep =
  (typeof GENERATED_APPLICATION_PERSISTENCE_ORDER)[number]

// ─── byte identity ──────────────────────────────────────────────────────

const CONTENT_HASH = /^sha256:[a-f0-9]{64}$/

/**
 * SHA-256 over raw bytes, through WebCrypto.
 *
 * Deliberately NOT `@cogo/publisher-core`'s `hashContent` (that one is
 * `node:crypto` over a canonical JSON string) and deliberately not a second
 * copy of Task 4's: `observe-generated-application.ts` imports THIS one,
 * so the address a given byte string gets is decided in exactly one place.
 * A second implementation would let the same bytes carry two addresses in
 * two evidence objects that are supposed to name the same thing.
 */
export async function sha256OfApplicationBytes(
  bytes: Uint8Array,
): Promise<ContentHash> {
  const copy = new Uint8Array(bytes.byteLength)
  copy.set(bytes)
  const digest = await crypto.subtle.digest('SHA-256', copy.buffer as ArrayBuffer)
  const hex = [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
  return `sha256:${hex}`
}

/** The single declaration of how generated bytes become an address. */
export async function generatedApplicationContentRef(
  kind: 'application-package' | 'generated-artifact',
  bytes: Uint8Array,
): Promise<ContentRef> {
  const contentHash = await sha256OfApplicationBytes(bytes)
  return { ref: `cogo-content:${kind}:${contentHash}`, contentHash }
}

/**
 * The address of a sealed envelope: its own `contentHash`, which the envelope
 * derived from its own body. Not an authority — an ADDRESS. The authority refs
 * on this lane (`modelInvocationRef`, `artifactValidationObservationRef`,
 * `artifactPersistenceEligibilityRef`) are all issued server-side and merely
 * carried here.
 */
export function generationEvidenceEnvelopeContentRef(
  envelope: ApplicationGenerationEvidenceEnvelope,
): ContentRef {
  return {
    ref: `cogo-content:generation-evidence-envelope:${envelope.contentHash}`,
    contentHash: envelope.contentHash,
  }
}

/**
 * The address of the write that just happened: what was written, where, under
 * which trace, and which rows it produced. Derived rather than invented, so
 * two different writes cannot share one address and the same write cannot be
 * given two.
 */
export function generatedApplicationPersistenceWriteRef(input: {
  projectId: string
  traceId: string
  generationEvidenceEnvelope: ApplicationGenerationEvidenceEnvelope
  writtenBlockIdMap: Record<string, string>
}): ContentRef {
  const contentHash = hashCanonicalContent({
    projectId: input.projectId,
    traceId: input.traceId,
    envelopeContentHash: input.generationEvidenceEnvelope.contentHash,
    blockIdMap: { ...input.writtenBlockIdMap },
  })
  return { ref: `cogo-content:persistence-write:${contentHash}`, contentHash }
}

const BASE64_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'

/**
 * Base64 without `btoa`/`Buffer`.
 *
 * `btoa` is a latin1 round trip and `Buffer` is Node-only; this lane runs in
 * the browser and carries opaque bytes that must survive verbatim, so the
 * codec is explicit and environment-free.
 */
export function encodeGeneratedApplicationBytes(bytes: Uint8Array): string {
  let out = ''
  for (let index = 0; index < bytes.length; index += 3) {
    const byte0 = bytes[index]!
    const byte1 = index + 1 < bytes.length ? bytes[index + 1]! : 0
    const byte2 = index + 2 < bytes.length ? bytes[index + 2]! : 0
    out += BASE64_ALPHABET[byte0 >> 2]
    out += BASE64_ALPHABET[((byte0 & 0b11) << 4) | (byte1 >> 4)]
    out +=
      index + 1 < bytes.length
        ? BASE64_ALPHABET[((byte1 & 0b1111) << 2) | (byte2 >> 6)]
        : '='
    out += index + 2 < bytes.length ? BASE64_ALPHABET[byte2 & 0b111111] : '='
  }
  return out
}

export function decodeGeneratedApplicationBytes(value: string): Uint8Array {
  if (value.length % 4 !== 0 || !/^[A-Za-z0-9+/]*={0,2}$/.test(value)) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.GENERATED_APPLICATION_BYTES_DRIFT,
      'base64',
    )
  }
  const padding = value.endsWith('==') ? 2 : value.endsWith('=') ? 1 : 0
  const out = new Uint8Array((value.length / 4) * 3 - padding)
  let cursor = 0
  for (let index = 0; index < value.length; index += 4) {
    const chunk = [0, 1, 2, 3].map((offset) => {
      const character = value[index + offset]!
      return character === '=' ? 0 : BASE64_ALPHABET.indexOf(character)
    })
    const triple =
      (chunk[0]! << 18) | (chunk[1]! << 12) | (chunk[2]! << 6) | chunk[3]!
    for (const [offset, shift] of [
      [0, 16],
      [1, 8],
      [2, 0],
    ] as const) {
      if (cursor + offset < out.length) {
        out[cursor + offset] = (triple >> shift) & 0xff
      }
    }
    cursor += 3
  }
  return out
}

/**
 * Realm-safe `Uint8Array` test.
 *
 * `instanceof` compares against THIS realm's constructor, and the bytes on
 * this lane are produced by whoever encoded them — a `TextEncoder` from
 * another realm in a jsdom test, a worker, a transferred buffer. An
 * `instanceof` check here rejects perfectly good bytes for where they were
 * made, which is a refusal about provenance dressed up as one about type.
 */
function isByteArray(value: unknown): value is Uint8Array {
  return Object.prototype.toString.call(value) === '[object Uint8Array]'
}

function sameBytes(left: Uint8Array, right: Uint8Array): boolean {
  if (left.byteLength !== right.byteLength) return false
  for (let index = 0; index < left.byteLength; index += 1) {
    if (left[index] !== right[index]) return false
  }
  return true
}

// ─── what a bound generation stores, and where ──────────────────────────

/**
 * The two settings keys a bound generation writes. Exported as a frozen pair
 * so the read side, the write side and the durability tests all name the
 * same two strings; a rename that reaches only one of the three is then a
 * failing test rather than a silently unread key.
 */
export const APPLICATION_ONTOLOGY_GENERATION_BINDING_SETTINGS_KEY =
  'applicationOntologyGenerationBinding'
export const APPLICATION_GENERATION_EVIDENCE_SETTINGS_KEY =
  'applicationGenerationEvidence'
export const APPLICATION_GENERATION_EVIDENCE_SETTINGS_KEYS = [
  APPLICATION_ONTOLOGY_GENERATION_BINDING_SETTINGS_KEY,
  APPLICATION_GENERATION_EVIDENCE_SETTINGS_KEY,
] as const

const EVIDENCE_RECORD_KEYS = [
  'generationEvidenceEnvelope',
  'generationEvidenceEnvelopeRef',
  'sourceTreeRef',
  'runtimeRef',
  'applicationPackageBytes',
  'artifactBytes',
] as const

export interface PersistedApplicationGenerationEvidence {
  generationEvidenceEnvelope: ApplicationGenerationEvidenceEnvelope
  generationEvidenceEnvelopeRef: ContentRef
  sourceTreeRef: ContentRef
  runtimeRef: ContentRef
  /** base64 of the exact generated executable application-package bytes. */
  applicationPackageBytes: string
  /** base64 of the exact generated artifact bytes. */
  artifactBytes: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  )
}

function isContentRef(value: unknown): value is ContentRef {
  return (
    isRecord(value) &&
    Object.keys(value).length === 2 &&
    typeof value.ref === 'string' &&
    value.ref.trim().length > 0 &&
    typeof value.contentHash === 'string' &&
    CONTENT_HASH.test(value.contentHash)
  )
}

function sameRef(left: ContentRef, right: ContentRef): boolean {
  return left.ref === right.ref && left.contentHash === right.contentHash
}

/**
 * The exact settings overlay a bound generation adds to the ONE metadata
 * write. Plain JSON only — the settings PATCH is a JSON body, and a value
 * that does not survive `JSON.stringify` verbatim is a value the readback
 * can never reconstruct.
 */
export function buildPersistedApplicationGenerationEvidenceSettings(input: {
  binding: ApplicationOntologyGenerationBinding
  generationEvidenceEnvelope: ApplicationGenerationEvidenceEnvelope
  generationEvidenceEnvelopeRef: ContentRef
  sourceTreeRef: ContentRef
  runtimeRef: ContentRef
  applicationPackageBytes: Uint8Array
  artifactBytes: Uint8Array
}): Record<string, unknown> {
  const evidence: PersistedApplicationGenerationEvidence = {
    generationEvidenceEnvelope: input.generationEvidenceEnvelope,
    generationEvidenceEnvelopeRef: { ...input.generationEvidenceEnvelopeRef },
    sourceTreeRef: { ...input.sourceTreeRef },
    runtimeRef: { ...input.runtimeRef },
    applicationPackageBytes: encodeGeneratedApplicationBytes(
      input.applicationPackageBytes,
    ),
    artifactBytes: encodeGeneratedApplicationBytes(input.artifactBytes),
  }
  return {
    [APPLICATION_ONTOLOGY_GENERATION_BINDING_SETTINGS_KEY]: JSON.parse(
      JSON.stringify(input.binding),
    ) as unknown,
    [APPLICATION_GENERATION_EVIDENCE_SETTINGS_KEY]: JSON.parse(
      JSON.stringify(evidence),
    ) as unknown,
  }
}

/**
 * Reads the persisted evidence record off ANY shape that carries a
 * `settings` record — an authenticated `loadProject()` result, or the raw
 * project row. Accepting `unknown` is deliberate: the caller is handing over
 * something that came back from the network, and every structural
 * expectation about it belongs here rather than at the call site.
 */
function readPersistedEvidenceRecord(
  project: unknown,
): { binding: unknown; evidence: Record<string, unknown> } {
  const settings = isRecord(project) ? project.settings : undefined
  if (!isRecord(settings)) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.GENERATION_EVIDENCE_ABSENT,
      'settings',
    )
  }
  const evidence = settings[APPLICATION_GENERATION_EVIDENCE_SETTINGS_KEY]
  if (!isRecord(evidence)) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.GENERATION_EVIDENCE_ABSENT,
      APPLICATION_GENERATION_EVIDENCE_SETTINGS_KEY,
    )
  }
  const keys = Object.keys(evidence)
  if (
    keys.length !== EVIDENCE_RECORD_KEYS.length ||
    !EVIDENCE_RECORD_KEYS.every((key) =>
      Object.prototype.hasOwnProperty.call(evidence, key),
    )
  ) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.GENERATION_EVIDENCE_READBACK_DRIFT,
      'evidence-keys',
    )
  }
  return {
    binding: settings[APPLICATION_ONTOLOGY_GENERATION_BINDING_SETTINGS_KEY],
    evidence,
  }
}

/**
 * Reconstructs the closed envelope from what the server holds.
 *
 * Re-derives the envelope's own `contentHash` from the reconstructed body
 * before returning it, so a body edited on the server with its stored hash
 * left intact is refused here rather than compared as equal downstream.
 */
export function readProjectApplicationGenerationEvidenceEnvelope(
  project: unknown,
): ApplicationGenerationEvidenceEnvelope {
  const { binding, evidence } = readPersistedEvidenceRecord(project)
  const envelope = evidence.generationEvidenceEnvelope
  if (!isRecord(envelope)) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.GENERATION_EVIDENCE_ABSENT,
      'generationEvidenceEnvelope',
    )
  }
  const { contentHash, ...body } = envelope
  if (typeof contentHash !== 'string' || hashCanonicalContent(body) !== contentHash) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.GENERATION_EVIDENCE_READBACK_DRIFT,
      'envelope-content-hash',
    )
  }
  // The binding is stored twice on purpose — once as `settings.applicationOntologyGenerationBinding`
  // (the thing the rest of Studio reads) and once inside the envelope (the
  // thing the evidence is about). If those two ever disagree, the project is
  // telling two stories about which generation it holds.
  if (
    stableCanonicalStringify(binding) !== stableCanonicalStringify(envelope.binding)
  ) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.GENERATION_EVIDENCE_READBACK_DRIFT,
      'settings-binding',
    )
  }
  try {
    return assertApplicationGenerationEvidenceEnvelopeIntegrity(
      envelope,
      evidenceRoleResolverForEnvelope(
        envelope as unknown as ApplicationGenerationEvidenceEnvelope,
      ),
    )
  } catch (error) {
    // Includes the case this program exists to catch: a readback in which
    // the INPUT ontology package and the GENERATED package have been
    // collapsed onto one address. The resolver is a map keyed by address,
    // so the two roles cannot both resolve and the closed contract refuses.
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.GENERATION_EVIDENCE_READBACK_DRIFT,
      error instanceof Error ? error.message : 'envelope-contract',
    )
  }
}

function readProjectBytes(
  project: unknown,
  field: 'applicationPackageBytes' | 'artifactBytes',
): Uint8Array {
  const { evidence } = readPersistedEvidenceRecord(project)
  const encoded = evidence[field]
  if (typeof encoded !== 'string') {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.GENERATION_EVIDENCE_ABSENT,
      field,
    )
  }
  return decodeGeneratedApplicationBytes(encoded)
}

export function readProjectGeneratedApplicationPackageBytes(
  project: unknown,
): Uint8Array {
  return readProjectBytes(project, 'applicationPackageBytes')
}

export function readProjectGeneratedApplicationArtifactBytes(
  project: unknown,
): Uint8Array {
  return readProjectBytes(project, 'artifactBytes')
}

function readProjectStagedRefs(project: unknown): {
  generationEvidenceEnvelopeRef: ContentRef
  sourceTreeRef: ContentRef
  runtimeRef: ContentRef
} {
  const { evidence } = readPersistedEvidenceRecord(project)
  const staged = {
    generationEvidenceEnvelopeRef: evidence.generationEvidenceEnvelopeRef,
    sourceTreeRef: evidence.sourceTreeRef,
    runtimeRef: evidence.runtimeRef,
  }
  for (const [field, value] of Object.entries(staged)) {
    if (!isContentRef(value)) {
      refuse(
        GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.GENERATION_EVIDENCE_READBACK_DRIFT,
        field,
      )
    }
  }
  return staged as {
    generationEvidenceEnvelopeRef: ContentRef
    sourceTreeRef: ContentRef
    runtimeRef: ContentRef
  }
}

// ─── the role table ─────────────────────────────────────────────────────

/**
 * Every evidence role compared across the write/read boundary.
 *
 * The brief names 28 comma-separated items; this list has 29 entries because
 * its item "KG snapshot/readback" names TWO refs, and collapsing them into
 * one comparison would let a drifted readback ref hide behind an intact
 * snapshot ref. Each member is compared on its own and each is exercised on
 * its own by an `it.each` in the suite, because a per-code assertion cannot
 * see intra-list attrition — dropping a member is invisible to every test
 * that does not enumerate the list itself.
 */
export const GENERATED_APPLICATION_READBACK_COMPARISON_ROLES = [
  'source-tree',
  'profile',
  'application-ontology-package',
  'application-package',
  'acceptance-manifest',
  'compiled-application-ontology',
  'required-capability-slice',
  'required-application-topology-slice',
  'required-capability-catalog',
  'runtime-consumer-catalog',
  'application-generation-eligibility',
  'profile-selection',
  'project-scope',
  'actor-scope',
  'host-capability-manifest',
  'validator',
  'compiler-identity',
  'prompt-template',
  'sealed-input',
  'kg-snapshot',
  'kg-readback',
  'model-policy',
  'model-invocation',
  'generated-artifact',
  'artifact-validation-observation',
  'artifact-persistence-eligibility',
  'runtime',
  'evidence-realm',
  'receipt-chain-head',
] as const satisfies readonly ApplicationEvidenceRole[]

export type GeneratedApplicationReadbackComparisonRole =
  (typeof GENERATED_APPLICATION_READBACK_COMPARISON_ROLES)[number]

/**
 * The binding fields this comparison covers, keyed by role. Derived from the
 * binding's own shape rather than hand-listed twice: `bindingRoleFields` is
 * the ONLY place a binding ref is associated with a role on this lane, and
 * the suite asserts every `*Ref` key of a real binding appears here, so a
 * binding that grows a field without a comparison entry fails a test instead
 * of silently travelling uncompared.
 */
const BINDING_ROLE_FIELDS = {
  profile: 'profileRef',
  'application-ontology-package': 'applicationOntologyPackageRef',
  'profile-selection': 'profileSelectionRef',
  'project-scope': 'projectScopeRef',
  'actor-scope': 'actorScopeRef',
  'acceptance-manifest': 'manifestRef',
  'compiled-application-ontology': 'compiledOntologyRef',
  'required-capability-slice': 'requiredCapabilitySliceRef',
  'required-application-topology-slice': 'requiredApplicationTopologySliceRef',
  'required-capability-catalog': 'requiredCapabilityCatalogRef',
  'runtime-consumer-catalog': 'runtimeConsumerCatalogRef',
  'application-generation-eligibility': 'applicationGenerationEligibilityRef',
  'host-capability-manifest': 'hostManifestRef',
  validator: 'validatorRef',
  'compiler-identity': 'compilerIdentityRef',
  'prompt-template': 'promptTemplateRef',
  'sealed-input': 'sealedInputRef',
  'model-policy': 'modelPolicyRef',
  'evidence-realm': 'evidenceRealmRef',
  'receipt-chain-head': 'receiptChainHeadRef',
} as const satisfies Partial<
  Record<
    GeneratedApplicationReadbackComparisonRole,
    keyof ApplicationOntologyGenerationBinding
  >
>

export const GENERATED_APPLICATION_READBACK_BINDING_ROLE_FIELDS: Readonly<
  Record<string, string>
> = BINDING_ROLE_FIELDS

const ENVELOPE_ROLE_FIELDS = {
  'model-invocation': 'modelInvocationRef',
  'application-package': 'applicationPackageRef',
  'generated-artifact': 'artifactRef',
  'artifact-validation-observation': 'artifactValidationObservationRef',
  'artifact-persistence-eligibility': 'artifactPersistenceEligibilityRef',
} as const satisfies Partial<
  Record<
    GeneratedApplicationReadbackComparisonRole,
    keyof ApplicationGenerationEvidenceEnvelope
  >
>

/**
 * The full role → ref projection for one side of the comparison. Both sides
 * go through this ONE function, so "which ref plays which role" cannot be
 * spelled differently for the written side and the read side.
 */
export function projectGeneratedApplicationEvidenceRoles(input: {
  envelope: ApplicationGenerationEvidenceEnvelope
  sourceTreeRef: ContentRef
  runtimeRef: ContentRef
}): Record<GeneratedApplicationReadbackComparisonRole, ContentRef> {
  const binding = input.envelope.binding
  const projected: Partial<
    Record<GeneratedApplicationReadbackComparisonRole, ContentRef>
  > = {
    'source-tree': input.sourceTreeRef,
    runtime: input.runtimeRef,
    'kg-snapshot': binding.kgPreflight.snapshotRef,
    'kg-readback': binding.kgPreflight.readbackRef,
  }
  for (const [role, field] of Object.entries(BINDING_ROLE_FIELDS) as [
    GeneratedApplicationReadbackComparisonRole,
    keyof ApplicationOntologyGenerationBinding,
  ][]) {
    projected[role] = binding[field] as ContentRef
  }
  for (const [role, field] of Object.entries(ENVELOPE_ROLE_FIELDS) as [
    GeneratedApplicationReadbackComparisonRole,
    keyof ApplicationGenerationEvidenceEnvelope,
  ][]) {
    projected[role] = input.envelope[field] as ContentRef
  }
  for (const role of GENERATED_APPLICATION_READBACK_COMPARISON_ROLES) {
    if (!isContentRef(projected[role])) {
      refuse(
        GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.EVIDENCE_ROLE_READBACK_DRIFT,
        role,
      )
    }
  }
  return projected as Record<
    GeneratedApplicationReadbackComparisonRole,
    ContentRef
  >
}

/**
 * A resolver built from the refs THIS lane actually used.
 *
 * Not a tautology: the map is keyed by the ref ADDRESS, so two roles holding
 * one address collapse to a single entry and one of the two role assertions
 * then fails. That is precisely the input/output package confusion this
 * program exists to catch, expressed as a data structure rather than as a
 * pair of hand-written inequalities.
 */
function evidenceRoleResolverForEnvelope(
  envelope: ApplicationGenerationEvidenceEnvelope,
): ApplicationEvidenceRoleResolver {
  // Keyed by `ref` + `\u0000` + `contentHash`. The separator is written as an
  // ESCAPE, never as a raw NUL byte in this file: a literal NUL in source is
  // invisible in a diff and makes plain `grep` skip the whole file as binary,
  // which is how a line stops being reviewable without anyone deciding it
  // should. As a separator it is still the right choice — no content ref can
  // contain U+0000, so no two distinct pairs can collide on one key.
  const table = new Map<string, ApplicationEvidenceRole>()
  const put = (reference: unknown, role: ApplicationEvidenceRole) => {
    if (!isContentRef(reference)) return
    table.set(`${reference.ref}\u0000${reference.contentHash}`, role)
  }
  const binding = envelope.binding
  for (const [role, field] of Object.entries(BINDING_ROLE_FIELDS) as [
    ApplicationEvidenceRole,
    keyof ApplicationOntologyGenerationBinding,
  ][]) {
    put(binding[field], role)
  }
  put(binding.kgPreflight?.observationRef, 'kg-observation')
  put(binding.kgPreflight?.producerAttestationRef, 'kg-producer-attestation')
  put(binding.kgPreflight?.snapshotRef, 'kg-snapshot')
  put(binding.kgPreflight?.readbackRef, 'kg-readback')
  for (const [role, field] of Object.entries(ENVELOPE_ROLE_FIELDS) as [
    ApplicationEvidenceRole,
    keyof ApplicationGenerationEvidenceEnvelope,
  ][]) {
    put(envelope[field], role)
  }
  return {
    roleOf(reference: ContentRef): ApplicationEvidenceRole {
      const role = table.get(`${reference.ref}\u0000${reference.contentHash}`)
      if (!role) throw new Error('unknown-evidence-ref')
      return role
    },
  }
}

// ─── the server-owned envelope ──────────────────────────────────────────

/**
 * Builds the closed envelope. There is no parameter through which a body,
 * a `contentHash`, or a `schemaVersion` can arrive: those three are decided
 * here, from the five refs and the immutable binding.
 */
export function createServerOwnedApplicationGenerationEvidenceEnvelope(input: {
  binding: ApplicationOntologyGenerationBinding
  modelInvocationRef: ContentRef
  applicationPackageRef: ContentRef
  artifactRef: ContentRef
  artifactValidationObservationRef: ContentRef
  artifactPersistenceEligibilityRef: ContentRef
}): ApplicationGenerationEvidenceEnvelope {
  // The INPUT ontology package and the GENERATED executable package are
  // different roles with identical shapes. A computed collision between
  // them — or between the package and the artifact — is refused outright
  // rather than sealed into evidence.
  const collisions: readonly [ContentRef, ContentRef, string][] = [
    [input.applicationPackageRef, input.binding.applicationOntologyPackageRef, 'input-package'],
    [input.applicationPackageRef, input.artifactRef, 'artifact'],
  ]
  for (const [left, right, detail] of collisions) {
    if (left.ref === right.ref || left.contentHash === right.contentHash) {
      refuse(
        GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.APPLICATION_PACKAGE_ROLE_COLLISION,
        detail,
      )
    }
  }
  const body = {
    schemaVersion: 'cogo.application-generation-evidence-envelope.v1' as const,
    binding: JSON.parse(
      JSON.stringify(input.binding),
    ) as ApplicationOntologyGenerationBinding,
    modelInvocationRef: { ...input.modelInvocationRef },
    applicationPackageRef: { ...input.applicationPackageRef },
    artifactRef: { ...input.artifactRef },
    artifactValidationObservationRef: { ...input.artifactValidationObservationRef },
    artifactPersistenceEligibilityRef: { ...input.artifactPersistenceEligibilityRef },
  }
  const envelope = {
    ...body,
    contentHash: hashCanonicalContent(body),
  } as ApplicationGenerationEvidenceEnvelope
  try {
    return assertApplicationGenerationEvidenceEnvelopeIntegrity(
      envelope,
      evidenceRoleResolverForEnvelope(envelope),
    )
  } catch (error) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.GENERATION_EVIDENCE_ENVELOPE_INVALID,
      error instanceof Error ? error.message : 'invalid',
    )
  }
}

// ─── the server-only subject ────────────────────────────────────────────

const PERSISTENCE_SUBJECT_KEYS = [
  'binding',
  'applicationPackageBytes',
  'artifactBytes',
  'artifactValidationObservationRef',
  'sourceTreeRef',
  'runtimeRef',
] as const

/**
 * What the artifact-valid admission hands persistence. It has EXACTLY six
 * keys and none of them is `modelInvocationRef`, `generationEvidenceEnvelope`
 * or `contentHash`: the check counts keys, so a browser-reachable path that
 * offers one is refused rather than having it quietly dropped.
 */
export interface OntologyGeneratedApplicationPersistenceSubject {
  binding: ApplicationOntologyGenerationBinding
  applicationPackageBytes: Uint8Array
  artifactBytes: Uint8Array
  artifactValidationObservationRef: ContentRef
  sourceTreeRef: ContentRef
  runtimeRef: ContentRef
}

export function assertServerOnlyPersistenceSubject(
  value: unknown,
): OntologyGeneratedApplicationPersistenceSubject {
  if (!isRecord(value)) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.SERVER_ONLY_FIELD_OFFERED,
      'subject',
    )
  }
  const keys = Object.keys(value)
  if (
    keys.length !== PERSISTENCE_SUBJECT_KEYS.length ||
    !PERSISTENCE_SUBJECT_KEYS.every((key) =>
      Object.prototype.hasOwnProperty.call(value, key),
    )
  ) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.SERVER_ONLY_FIELD_OFFERED,
      keys.sort().join(','),
    )
  }
  if (
    !isByteArray(value.applicationPackageBytes) ||
    !isByteArray(value.artifactBytes) ||
    !isContentRef(value.artifactValidationObservationRef) ||
    !isContentRef(value.sourceTreeRef) ||
    !isContentRef(value.runtimeRef) ||
    !isRecord(value.binding)
  ) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.SERVER_ONLY_FIELD_OFFERED,
      'subject-shape',
    )
  }
  return value as unknown as OntologyGeneratedApplicationPersistenceSubject
}

// ─── Task 3's verified terminal result evidence ─────────────────────────

export const APPLICATION_ONTOLOGY_GENERATION_RESULT_EVIDENCE_SCHEMA_VERSION =
  'cogo.application-ontology-generation-result-evidence.v1'

export const APPLICATION_ONTOLOGY_GENERATION_RESULT_EVIDENCE_COMPLETION_KEY =
  'applicationOntologyGenerationEvidence'

/**
 * The ONLY legitimate source of `modelInvocationRef`.
 *
 * That ref exists because a model actually ran, and it is a content hash of
 * the bounded model review record — so it provably cannot exist before the
 * call. Task 3's terminal exits are where it is verified against that record;
 * this reader's job is to take it off the completion payload and refuse
 * everything else, in particular:
 *
 *   - a completion that carries no evidence at all for a BOUND generation
 *     (the missing-receipt compatibility path — closed here, deliberately);
 *   - evidence whose echoed binding is not the binding being persisted, which
 *     would attach a real invocation to the wrong application.
 *
 * The comparison is key-order-insensitive because the evidence crossed a JSON
 * transport; it is otherwise exact.
 */
export function readVerifiedTerminalModelInvocationRef(
  completion: unknown,
  expectedBinding: ApplicationOntologyGenerationBinding,
): ContentRef {
  const evidence = isRecord(completion)
    ? completion[APPLICATION_ONTOLOGY_GENERATION_RESULT_EVIDENCE_COMPLETION_KEY]
    : undefined
  if (!isRecord(evidence)) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.TERMINAL_RESULT_EVIDENCE_ABSENT,
      APPLICATION_ONTOLOGY_GENERATION_RESULT_EVIDENCE_COMPLETION_KEY,
    )
  }
  const keys = Object.keys(evidence)
  if (
    keys.length !== 3 ||
    evidence.schemaVersion !==
      APPLICATION_ONTOLOGY_GENERATION_RESULT_EVIDENCE_SCHEMA_VERSION ||
    !isContentRef(evidence.modelInvocationRef) ||
    !isRecord(evidence.binding)
  ) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.TERMINAL_RESULT_EVIDENCE_INVALID,
      'shape',
    )
  }
  if (
    stableCanonicalStringify(evidence.binding) !==
    stableCanonicalStringify(expectedBinding)
  ) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.TERMINAL_RESULT_EVIDENCE_INVALID,
      'binding',
    )
  }
  return {
    ref: evidence.modelInvocationRef.ref,
    contentHash: evidence.modelInvocationRef.contentHash,
  }
}

// ─── the generic block-identity readback comparison ──────────────────────

/**
 * MOVED here from `register-studio-tools.ts` (Task 5, Step 4).
 *
 * The two generic pieces of the authenticated `loadProject()` comparison —
 * "these are exactly the expected names" and "these are exactly the expected
 * row identities". They were private to a 10k-line MCP registration file and
 * were reachable only by running a whole generation; they are pure functions
 * over a readback and belong on the persistence seam, where the readback is.
 * `register-studio-tools.ts` imports both back.
 */
export function hasExactStringMembers(
  actual: readonly string[],
  expected: readonly string[],
): boolean {
  if (actual.length !== expected.length || new Set(actual).size !== actual.length) {
    return false
  }
  const actualMembers = new Set(actual)
  return expected.every((item) => actualMembers.has(item))
}

export function assertGeneratedApplicationBlockIdentityReadback(input: {
  expectedBlockNames: readonly string[]
  writtenBlockIdMap: Record<string, string>
  readbackBlockNames: readonly string[]
  readbackBlockIdMap: Record<string, string>
}): void {
  if (
    !hasExactStringMembers(input.readbackBlockNames, input.expectedBlockNames) ||
    !hasExactStringMembers(Object.keys(input.writtenBlockIdMap), input.expectedBlockNames) ||
    !hasExactStringMembers(Object.keys(input.readbackBlockIdMap), input.expectedBlockNames)
  ) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.GENERATED_APPLICATION_READBACK_DRIFT,
      'block-names',
    )
  }
  for (const blockName of input.expectedBlockNames) {
    const expectedBlockId = input.writtenBlockIdMap[blockName]
    if (
      typeof expectedBlockId !== 'string' ||
      !expectedBlockId ||
      input.readbackBlockIdMap[blockName] !== expectedBlockId
    ) {
      refuse(
        GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.GENERATED_APPLICATION_READBACK_DRIFT,
        'block-identities',
      )
    }
  }
}

// ─── persist once, read back independently ──────────────────────────────

export interface PersistGeneratedApplicationWrite {
  projectId: string
  traceId: string
  artifact: LoadedApp
  applicationPackageBytes: Uint8Array
  artifactBytes: Uint8Array
  binding: ApplicationOntologyGenerationBinding
  generationEvidenceEnvelope: ApplicationGenerationEvidenceEnvelope
}

export type FreshProjectRead = (
  projectId: string,
  traceId?: string,
) => Promise<{
  app: LoadedApp
  blockIdMap: Record<string, string>
  settings: Record<string, unknown> | null
}>

export interface PersistAndReadBackGeneratedApplicationInput {
  projectId: string
  traceId: string
  artifact: LoadedApp
  applicationPackageBytes: Uint8Array
  artifactBytes: Uint8Array
  binding: ApplicationOntologyGenerationBinding
  modelInvocationRef: ContentRef
  artifactValidationObservationRef: ContentRef
  artifactPersistenceEligibilityRef: ContentRef
  sourceTreeRef: ContentRef
  runtimeRef: ContentRef
  /**
   * The row identities come back FROM the write, not in with the input, and
   * they are REQUIRED.
   *
   * A caller-supplied map is a memory of what the caller believes it asked
   * for; the readback question is what the write actually produced, and only
   * the write can answer that. Required rather than optional because the
   * optional form makes "the write reported nothing" indistinguishable from
   * "the comparison passed" — the type says it must be reported, and the seam
   * refuses at runtime if it is not, so a JS caller cannot skip it either.
   */
  write: (input: PersistGeneratedApplicationWrite) => Promise<{
    persistenceWriteRef: ContentRef
    generationEvidenceEnvelopeRef: ContentRef
    writtenBlockIdMap: Record<string, string>
  }>
  freshRead: FreshProjectRead
  appendObservation: (
    observation: GeneratedApplicationReadbackObservation,
  ) => Promise<ContentRef>
  onPersistenceStep?: (step: GeneratedApplicationPersistenceStep) => void
}

export async function persistAndReadBackGeneratedApplication(
  input: PersistAndReadBackGeneratedApplicationInput,
): Promise<GeneratedApplicationReadbackObservation> {
  const step = (name: GeneratedApplicationPersistenceStep) =>
    input.onPersistenceStep?.(name)

  // 1. SEAL. Both content refs are computed HERE, from the exact bytes.
  const applicationPackageRef = await generatedApplicationContentRef(
    'application-package',
    input.applicationPackageBytes,
  )
  const artifactRef = await generatedApplicationContentRef(
    'generated-artifact',
    input.artifactBytes,
  )
  const envelope = createServerOwnedApplicationGenerationEvidenceEnvelope({
    binding: input.binding,
    modelInvocationRef: input.modelInvocationRef,
    applicationPackageRef,
    artifactRef,
    artifactValidationObservationRef: input.artifactValidationObservationRef,
    artifactPersistenceEligibilityRef: input.artifactPersistenceEligibilityRef,
  })
  step('seal-generation-evidence-envelope')

  // 2. WRITE, exactly once. `persist-blocks-…` is deliberately NOT emitted
  //    here: the suites observe it from the write itself, so the array
  //    records a call that happened rather than a label this file printed.
  const written = await input.write({
    projectId: input.projectId,
    traceId: input.traceId,
    artifact: input.artifact,
    applicationPackageBytes: input.applicationPackageBytes,
    artifactBytes: input.artifactBytes,
    binding: input.binding,
    generationEvidenceEnvelope: envelope,
  })
  if (
    !isContentRef(written?.persistenceWriteRef) ||
    !isContentRef(written?.generationEvidenceEnvelopeRef)
  ) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.PERSISTENCE_WRITE_REF_DRIFT,
      'write-result',
    )
  }
  if (written.generationEvidenceEnvelopeRef.contentHash !== envelope.contentHash) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.PERSISTENCE_WRITE_REF_DRIFT,
      'envelope-ref',
    )
  }

  // 3. FRESH READ — a distinct authenticated call, issued after the write
  //    resolved. Nothing captured before the write is admissible here: an
  //    object the writer already held cannot testify about the server.
  const freshProject = await input.freshRead(input.projectId, input.traceId)
  step('fresh-load-project')

  // 4. RECONSTRUCT and compare.
  const readbackEnvelope = readProjectApplicationGenerationEvidenceEnvelope(freshProject)
  const readbackPackageBytes = readProjectGeneratedApplicationPackageBytes(freshProject)
  const readbackArtifactBytes = readProjectGeneratedApplicationArtifactBytes(freshProject)
  const stagedRefs = readProjectStagedRefs(freshProject)

  // ORDER MATTERS HERE, and getting it wrong is how this comparison dies
  // silently. The whole-envelope canonical equality below subsumes every
  // ref inside the envelope, so if it ran FIRST the 29-role loop could
  // never fire for any of them and would be decorative — a list of 29
  // assertions none of which can fail. The specific comparison therefore
  // runs first and names the role that drifted; the canonical equality is
  // the BACKSTOP for envelope content no role covers (a `kgPreflight` hash
  // string, say), and the suite proves the backstop is reachable rather
  // than assuming it.
  const expectedRoles = projectGeneratedApplicationEvidenceRoles({
    envelope,
    sourceTreeRef: input.sourceTreeRef,
    runtimeRef: input.runtimeRef,
  })
  const observedRoles = projectGeneratedApplicationEvidenceRoles({
    envelope: readbackEnvelope,
    sourceTreeRef: stagedRefs.sourceTreeRef,
    runtimeRef: stagedRefs.runtimeRef,
  })
  for (const role of GENERATED_APPLICATION_READBACK_COMPARISON_ROLES) {
    if (!sameRef(expectedRoles[role], observedRoles[role])) {
      refuse(
        GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.EVIDENCE_ROLE_READBACK_DRIFT,
        role,
      )
    }
  }

  if (
    stableCanonicalStringify(readbackEnvelope) !== stableCanonicalStringify(envelope) ||
    readbackEnvelope.contentHash !== envelope.contentHash
  ) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.GENERATION_EVIDENCE_READBACK_DRIFT,
      'envelope',
    )
  }
  if (!sameRef(stagedRefs.generationEvidenceEnvelopeRef, written.generationEvidenceEnvelopeRef)) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.GENERATION_EVIDENCE_READBACK_DRIFT,
      'staged-envelope-ref',
    )
  }

  // Byte arrays as byte arrays. `toEqual` over a JSON projection is
  // satisfied by a re-serialization; a one-byte change is not.
  if (!sameBytes(readbackPackageBytes, input.applicationPackageBytes)) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.GENERATED_APPLICATION_BYTES_DRIFT,
      'application-package',
    )
  }
  if (!sameBytes(readbackArtifactBytes, input.artifactBytes)) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.GENERATED_APPLICATION_BYTES_DRIFT,
      'artifact',
    )
  }
  // The readback bytes are ADDRESSED here because the observation needs those
  // two hashes below. There is deliberately NO comparison of these addresses
  // against the readback envelope's refs: the role loop above has already
  // equated `readbackEnvelope.applicationPackageRef` with
  // `envelope.applicationPackageRef`, and `sameBytes` has already equated
  // these bytes with the input bytes that ref was computed from — so such a
  // comparison could not fail for any input, in any order of server
  // misbehaviour. It was written here once and removed on review: an
  // assertion that cannot fail reads as a defense and is not one.
  const readbackPackageRef = await generatedApplicationContentRef(
    'application-package',
    readbackPackageBytes,
  )
  const readbackArtifactRef = await generatedApplicationContentRef(
    'generated-artifact',
    readbackArtifactBytes,
  )

  // Blocks and their row identities. REQUIRED, not conditional: a write that
  // reports no rows cannot be compared against, and "cannot be compared" is a
  // failure of this readback, not a reason to skip it. The optional form that
  // stood here made an absent map a silent pass — the same silent-skip class
  // this whole program is about, one level down from where it was first found.
  if (!isRecord(written.writtenBlockIdMap)) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.GENERATED_APPLICATION_READBACK_DRIFT,
      'written-block-ids-absent',
    )
  }
  assertGeneratedApplicationBlockIdentityReadback({
    expectedBlockNames: Object.keys(
      (input.artifact as unknown as { blocks?: Record<string, unknown> }).blocks ?? {},
    ),
    writtenBlockIdMap: written.writtenBlockIdMap as Record<string, string>,
    readbackBlockNames: Object.keys(
      (freshProject.app as unknown as { blocks?: Record<string, unknown> })?.blocks ?? {},
    ),
    readbackBlockIdMap: freshProject.blockIdMap ?? {},
  })

  // 5. OBSERVE the readback. `freshReadbackRef` addresses the READ OPERATION
  //    (what came back and under whose identity); `readbackArtifactRef`
  //    addresses the BYTES that came back. Two roles, two refs — the Task 1
  //    contract distinguishes them and so does this.
  const freshReadbackHash = hashCanonicalContent({
    projectId: input.projectId,
    traceId: input.traceId,
    envelopeContentHash: readbackEnvelope.contentHash,
    applicationPackageHash: readbackPackageRef.contentHash,
    artifactHash: readbackArtifactRef.contentHash,
    blockIdMap: freshProject.blockIdMap ?? {},
  })
  const freshReadbackRef: ContentRef = {
    ref: `cogo-content:fresh-readback:${freshReadbackHash}`,
    contentHash: freshReadbackHash,
  }
  const generationEvidenceEnvelopeRef = { ...written.generationEvidenceEnvelopeRef }
  const observationBody = {
    schemaVersion: 'cogo.generated-application-readback-observation.v1' as const,
    binding: envelope.binding,
    generationEvidenceEnvelopeRef,
    applicationPackageRef: { ...readbackEnvelope.applicationPackageRef },
    artifactRef: { ...readbackEnvelope.artifactRef },
    artifactValidationObservationRef: {
      ...readbackEnvelope.artifactValidationObservationRef,
    },
    artifactPersistenceEligibilityRef: {
      ...readbackEnvelope.artifactPersistenceEligibilityRef,
    },
    sourceTreeRef: { ...stagedRefs.sourceTreeRef },
    runtimeRef: { ...stagedRefs.runtimeRef },
    persistenceWriteRef: { ...written.persistenceWriteRef },
    freshReadbackRef,
    readbackArtifactRef: {
      ref: `cogo-content:readback-artifact:${readbackArtifactRef.contentHash}`,
      contentHash: readbackArtifactRef.contentHash,
    },
  }
  const observation = {
    ...observationBody,
    contentHash: hashCanonicalContent(observationBody),
  }
  const resolver = readbackObservationRoleResolver({
    envelope: readbackEnvelope,
    generationEvidenceEnvelopeRef,
    sourceTreeRef: stagedRefs.sourceTreeRef,
    runtimeRef: stagedRefs.runtimeRef,
    persistenceWriteRef: written.persistenceWriteRef,
    freshReadbackRef,
    readbackArtifactRef: observationBody.readbackArtifactRef,
  })
  let checked: GeneratedApplicationReadbackObservation
  try {
    checked = assertGeneratedApplicationReadbackObservationIntegrity(
      observation,
      resolver,
    )
  } catch (error) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.EVIDENCE_ROLE_READBACK_DRIFT,
      error instanceof Error ? error.message : 'observation',
    )
  }

  // 6. APPEND. Until this resolves with a ref naming THIS observation, the
  //    generation is not complete — an observation nobody recorded is not
  //    evidence, so an append failure is a failed generation.
  const appendedRef = await input.appendObservation(checked)
  if (!isContentRef(appendedRef) || appendedRef.contentHash !== checked.contentHash) {
    refuse(
      GENERATED_APPLICATION_PERSISTENCE_REFUSAL_CODES.READBACK_OBSERVATION_APPEND_DRIFT,
      'observation-ref',
    )
  }
  step('append-readback-observation')
  return checked
}

function readbackObservationRoleResolver(input: {
  envelope: ApplicationGenerationEvidenceEnvelope
  generationEvidenceEnvelopeRef: ContentRef
  sourceTreeRef: ContentRef
  runtimeRef: ContentRef
  persistenceWriteRef: ContentRef
  freshReadbackRef: ContentRef
  readbackArtifactRef: ContentRef
}): ApplicationEvidenceRoleResolver {
  const base = evidenceRoleResolverForEnvelope(input.envelope)
  const extra = new Map<string, ApplicationEvidenceRole>([
    [
      `${input.generationEvidenceEnvelopeRef.ref}\u0000${input.generationEvidenceEnvelopeRef.contentHash}`,
      'generation-evidence-envelope',
    ],
    [`${input.sourceTreeRef.ref}\u0000${input.sourceTreeRef.contentHash}`, 'source-tree'],
    [`${input.runtimeRef.ref}\u0000${input.runtimeRef.contentHash}`, 'runtime'],
    [
      `${input.persistenceWriteRef.ref}\u0000${input.persistenceWriteRef.contentHash}`,
      'persistence-write',
    ],
    [
      `${input.freshReadbackRef.ref}\u0000${input.freshReadbackRef.contentHash}`,
      'fresh-readback',
    ],
    [
      `${input.readbackArtifactRef.ref}\u0000${input.readbackArtifactRef.contentHash}`,
      'readback-artifact',
    ],
  ])
  return {
    roleOf(reference: ContentRef): ApplicationEvidenceRole {
      const key = `${reference.ref}\u0000${reference.contentHash}`
      const role = extra.get(key)
      if (role) return role
      return base.roleOf(reference)
    },
  }
}
